/**
 * Where a run-scoped page lives, and what to call the ViewPoint it shows.
 *
 * One place for the URL shapes so the left panel, the pages and the links between them cannot
 * drift. A null run means "the latest", addressed by the project-level route — which resolves to a
 * run server-side, and is what every link uses when no ViewPoint has been chosen.
 */

import type { Project } from 'types/project';

/** Run ids oldest first, so a run's position is the number the projects list shows on its chip. */
export function viewpointRunIds(project: Project | null | undefined): string[] {
  const runs = (project?.pipelineState?.runs as Record<string, unknown> | undefined) ?? {};
  return Object.keys(runs).sort((a, b) => Number(a) - Number(b));
}

/** "ViewPoint 3" for a chosen run; for null, the latest run's number. Null if there are no runs. */
export function viewpointNumber(project: Project | null | undefined, runId: string | null): number | null {
  const ids = viewpointRunIds(project);
  if (!ids.length) return null;
  const index = runId ? ids.indexOf(runId) : ids.length - 1;
  return index >= 0 ? index + 1 : null;
}

export const dashboardPath = (projectId: string, runId: string | null) =>
  runId ? `/projects/${projectId}/viewpoint/${runId}` : `/projects/${projectId}/dashboard`;

export const cognitivesPath = (projectId: string, runId: string | null, cogCode?: string) =>
  `${runId ? `/projects/${projectId}/viewpoint/${runId}` : `/projects/${projectId}`}/cognitives${cogCode ? `/${cogCode}` : ''}`;

export const goalsPath = (projectId: string, runId: string | null, goalCode?: string) =>
  `${runId ? `/projects/${projectId}/viewpoint/${runId}` : `/projects/${projectId}`}/goals${goalCode ? `/${goalCode}` : ''}`;
